import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';

import { OddsOperations } from '@frontend/sports/odds/feature';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { Store } from '@ngrx/store';
import { Subscription, filter, map, withLatestFrom } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { OverAskFlowState, OverAskMarketTypeChange } from '../../../model/over-ask/over-ask';
import { ComboSlipType, SingleSlipType } from '../../bet-generation/slip-type';
import { BetslipSummaryService } from '../../summary/services/betslip-summary.service';
import { BetslipOverAskActions } from '../actions';
import { IBetslipOverAskSummaryComponentProps, IBetslipOverAskSummaryViewModel } from '../models/over-ask.model';
import { overAskFlowStateSelector } from '../selectors';
import { OverAskTrackingActions } from '../tracking.actions';

@Component({
    template: '',
    standalone: false,
})
export abstract class BetslipOverAskSummaryBaseComponent implements OnInit, OnDestroy {
    private betslipStateSubscription: Subscription | undefined;
    private flowStateSubscription: Subscription | undefined;

    @Input() props: IBetslipOverAskSummaryComponentProps;
    @Input() oddsDisplayFormat: OddsDisplayFormat;

    abstract messages: {
        stakeLabel: string;
        cancelButtonTitle: string;
        maxReturnLabel: string;
        netMaxReturnLabel: string;
        newStake: string;
        newOdds: string;
        winMarketAbbr: string;
        eachWayAbbr: string;
    };

    vm: IBetslipOverAskSummaryViewModel = {
        original: {
            maxReturn: null,
            totalStake: 0,
            totalOdds: null,
            averageOdds: null,
            maxReturnNet: null,
            taxAmount: 0,
            taxRate: null,
        },
        offer: {
            maxReturn: null,
            totalStake: 0,
            totalOdds: null,
            averageOdds: null,
            taxAmount: 0,
            maxReturnNet: null,
            taxRate: null,
        },
    };

    isMultiBet = false;
    hasTax = false;
    isSingleBet = false;
    marketTypeOffer: OverAskMarketTypeChange | undefined;
    isComboBet = false;

    get isOneSlip(): boolean {
        return this.props.bets.length === 1; // When we have only one slip, one single/combo/system
    }

    /**
     * There is stake change when one bet and original stake is different from offer stake
     */
    get hasStakeChange(): boolean {
        return this.isOneSlip && this.vm.original.totalStake !== this.vm.offer.totalStake;
    }

    /**
     * We can show total odds changes when there were original total and current total odds
     */
    get hasOddsChange(): boolean {
        return (
            !!(this.vm.original.totalOdds && this.vm.offer.totalOdds) && !OddsOperations.equal2(this.vm.original.totalOdds, this.vm.offer.totalOdds)
        );
    }

    get hasChange(): boolean {
        return this.hasStakeChange || this.hasOddsChange;
    }

    get marketTypeOfferText(): string {
        return this.marketTypeOffer === OverAskMarketTypeChange.Win ? this.messages.winMarketAbbr : this.messages.eachWayAbbr;
    }

    get oldMarketTypeText(): string {
        if (!this.marketTypeOffer) {
            return '';
        }

        return this.marketTypeOffer === OverAskMarketTypeChange.Win ? this.messages.eachWayAbbr : this.messages.winMarketAbbr;
    }

    protected constructor(
        protected changeRef: ChangeDetectorRef,
        private betslipSummaryService: BetslipSummaryService,
        private store: Store<IBetslipRootState>,
    ) {}

    ngOnInit(): void {
        this.betslipStateSubscription = this.betslipSummaryService
            .getOverAskSummary$()
            .pipe(
                withLatestFrom(this.store.select(overAskFlowStateSelector)),
                filter(([_, flowState]) => flowState !== OverAskFlowState.None),
                map(([summary]) => summary),
            )
            .subscribe((value) => {
                const offerBetslipSummary = value.offer[1];
                const betslip = value.offer[0];
                this.isMultiBet = offerBetslipSummary.betCount > 1;
                this.isSingleBet = betslip.slips.length === 1 && betslip.slips[0]?.type instanceof SingleSlipType;
                this.isComboBet = betslip.slips.length === 1 && betslip.slips[0]?.type instanceof ComboSlipType;
                // When only one slip and one bet with multiple legs we can show total Odds.
                this.vm.offer.totalOdds = OddsOperations.isOddsEmpty(offerBetslipSummary.totalOdds) ? null : offerBetslipSummary.totalOdds;

                if (this.isMultiBet && offerBetslipSummary.possibleWinnings && offerBetslipSummary.totalStake) {
                    this.vm.offer.averageOdds = offerBetslipSummary.possibleWinnings.dividedBy(offerBetslipSummary.totalStake);
                }

                this.vm.offer.totalStake = offerBetslipSummary.totalStake!.toNumber(); // For over ask total stake will be always available
                this.vm.offer.maxReturn = offerBetslipSummary.possibleWinnings?.toNumber() || null;

                this.vm.offer.taxAmount = offerBetslipSummary.taxation?.amount?.toNumber() || 0;
                this.vm.offer.taxRate = offerBetslipSummary.taxation?.rate?.toNumber() || null;
                this.hasTax = this.vm.offer.taxAmount > 0;
                this.vm.offer.maxReturnNet = offerBetslipSummary.possibleWinningsNet?.toNumber() || null;

                const originalBetslipSummary = value.original[1];
                this.vm.original.totalOdds = OddsOperations.isOddsEmpty(originalBetslipSummary.totalOdds) ? null : originalBetslipSummary.totalOdds;
                this.vm.original.totalStake = originalBetslipSummary.totalStake!.toNumber(); // For over ask total stake will be always available
                this.vm.original.maxReturn = originalBetslipSummary.possibleWinnings?.toNumber() || null;
                this.changeRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this.betslipStateSubscription && this.betslipStateSubscription.unsubscribe();
        this.flowStateSubscription?.unsubscribe();
    }

    cancelOverAsk(): void {
        this.store.dispatch(BetslipOverAskActions.userRequestRejectOffer());
        this.store.dispatch(OverAskTrackingActions.trackClose({ method: 'cancel' }));
    }
}
